import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { DataSourceStatus, Datasource } from '@Core/services/proprietary/datasource';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import {
    AddFiling,
    DeleteNewFilings,
    NewDocument,
    PrepareRequest,
    ResetState,
    SetDefaultDrawerFolder,
    SetObjectState,
    UpdateDocument,
    UpdateFiling,
    UpdateFilingReferences
} from './document-object.actions';
import { denormalize, normalize, rootSchema } from './document-object.normalize';
import { DocumentObjectState } from './document-object.state';

export enum EventType {
    Update = 'Update',
    Create = 'Create'
}

@Injectable()
export class DocumentObjectService implements OnDestroy {
    private dsDocumentGetSub: Subscription;
    private dsDocumentProcessSub: Subscription;
    private docID: any = '';

    constructor(
        private store: Store,
        private app: AppService,
        private helper: StateHelperService,
        private router: Router,
        private ds: DataService
    ) { }

    ngOnDestroy() { }

    loadDocument(docID: any) {
        this.app.dsDocumentProcess.reset(); //clear process datastore (resets error messages)
        this.app.dsDocumentGet.reset();
        this.docID = docID;
        if (docID) {
            this.reset();
            this.dsDocumentGetSub = this.app.dsDocumentGet.status$.subscribe((x) => {
                this.documentGetStatusUpdated(x);
            });
            this.app.dsDocumentGet.get({ json: 'CT2', Doc_ID: docID, View: 'Full', Doc_Format: 'NONE' });
        } else {
            this.newDocument();
        }
    }

    newDocument(documentOptions?: any) {
        this.store.dispatch(new NewDocument(documentOptions));
    }

    process(event: EventType | EventType[], tranOptions?: any) {
        let eventStr: string = null;

        if (Array.isArray(event)) {
            let events: EventType[] = event;
            events.forEach(function (item) {
                eventStr = eventStr ? (eventStr = eventStr + ';' + item) : item;
            });
        } else {
            eventStr = event;
        }

        if (!this.docID) {
            eventStr = eventStr.replace('Update', 'Create');
        }

        tranOptions = { ...tranOptions, ...{ Event: eventStr } };

        this.store.dispatch(new PrepareRequest(tranOptions));

        let stateEntities = this.store.selectSnapshot(DocumentObjectState.entities);

        stateEntities = cloneDeep(stateEntities);

        //TODO:  Change SDK to allow negative ID's  -- treat them as new and don't validate exisitence
        if (stateEntities.Document && stateEntities.Document['-1']) {
            delete stateEntities.Document['-1'].Doc_ID;
        }

        let document = null;
        for (var i in stateEntities.Document) {
            document = stateEntities.Document[i];
            break;
        }

        for (var i in stateEntities.Filing) {
            if (i.slice(0, 1) == '-') {
                delete stateEntities.Filing[i].DocLink_ID;
            }
        }

        // Remove unselected new filing records
        for (var i in stateEntities.Filing) {
            if (i.slice(0, 1) == '-' && stateEntities.Filing[i].Selected_Ind !== 'Y') {
                document.Filing = this.helper.removeArrayItemByValue(document.Filing, i);
            }
        }

        //// need to listen for state change or does selectSnapshot wait until dispatched action have completed?

        let tran = denormalize('1', rootSchema, stateEntities);

        this.dsDocumentProcessSub = this.app.dsDocumentProcess.status$.subscribe((x) => {
            this.documentProcessStatusUpdated(x);
        });
        this.app.dsDocumentProcess.process({ json: 'CT2' }, tran);
    }

    documentProcessStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                this.dsDocumentProcessSub.unsubscribe();
                if (!this.docID || this.docID !== this.app.dsDocumentProcess.objectNode.Doc_ID) {
                    this.docID = this.app.dsDocumentProcess.objectNode.Doc_ID; //get the DocID from the reply -- task created
                }
                this.loadDocument(this.docID); //re-retrive   TODO: is there a better way to refresh data?   Should process automatically do get?
                break;
            case DataSourceStatus.Error:
                this.dsDocumentProcessSub.unsubscribe();
                this.load(this.app.dsDocumentGet.data); //reload old data
                break;
        }
    }

    documentGetStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                this.dsDocumentGetSub.unsubscribe(), this.load(this.app.dsDocumentGet.data);
                break;
            case DataSourceStatus.Error:
                this.dsDocumentGetSub.unsubscribe();
                break;
        }
    }

    async load(data: any) {
        let normalizedData = normalize(data, rootSchema);
        this.store.dispatch(new SetObjectState(normalizedData.entities));
        await this.getDefaultDrawerFolder(this.app.dsDocumentGet.objectNode.Doc_Class);
    }

    async getDefaultDrawerFolder(docClass: string) {
        return new Promise(async (resolve) => {
            if (docClass && docClass.length > 0) {
                let dsDefaults: Datasource = this.ds.addNewDatasource('CodeSet');
                dsDefaults.get({ Set_Code: 'DocClass', Code: docClass, Code_Mode: 'Equals' });

                let dsDefaultsSub: Subscription = dsDefaults.status$.subscribe((status) => {
                    switch (status) {
                        case DataSourceStatus.Ready:
                            if (dsDefaultsSub) {
                                dsDefaultsSub.unsubscribe();
                            }

                            if (dsDefaults?.objectNode?.CodeItems?.CodeItem.length == 1) {
                                let code = dsDefaults.objectNode.CodeItems.CodeItem[0];
                                let defaults = { File_Drawer: '', File_Folder: '' };

                                defaults.File_Drawer = code.Option_Code_2 || '?';
                                defaults.File_Folder = code.Option_Code_3 || '?';

                                this.setDefaultDrawerFolder(defaults, false);
                            }
                            if (dsDefaults) {
                                this.ds.deleteDatasource(dsDefaults);
                            }
                            resolve(true);
                            break;
                        case DataSourceStatus.Error:
                            if (dsDefaultsSub) {
                                dsDefaultsSub.unsubscribe();
                            }
                            if (dsDefaults) {
                                this.ds.deleteDatasource(dsDefaults);
                            }
                            resolve(true);
                            break;
                    }
                });
            } else {
                resolve(true);
            }
        });
    }

    reset() {
        this.store.dispatch(new ResetState());
    }

    updateDocument(document: any) {
        this.store.dispatch(new UpdateDocument(document));
    }

    updateFiling(filing: any) {
        this.store.dispatch(new UpdateFiling(filing));
    }

    addFiling(filing?: any) {
        this.store.dispatch(new AddFiling(filing));
    }

    deleteNewFilings() {
        this.store.dispatch(new DeleteNewFilings());
    }

    updateFilingReferences(filingTemplate: any) {
        this.store.dispatch(new UpdateFilingReferences(filingTemplate));
    }
    setDefaultDrawerFolder(defaults: any, updateFilings: boolean) {
        this.store.dispatch(new SetDefaultDrawerFolder(defaults, updateFilings));
    }
}
